/**
 * Platform Logos & Brand Config
 *
 * Centralized SVG logos and brand colors for all advertising platforms.
 * Use these instead of inline letter badges or emoji icons.
 *
 * Usage:
 *   import { PlatformLogo, platformBrand } from '@/components/icons/PlatformLogos';
 *   <PlatformLogo platform="meta" size="md" />
 */

import React from 'react';

export type PlatformKey = 'meta' | 'google_ads' | 'tiktok' | 'pinterest' | 'twitter';

export interface PlatformBrandConfig {
  /** Tailwind bg class for the platform */
  bg: string;
  /** Tailwind hover bg class */
  hover: string;
  /** Hex color value */
  color: string;
  /** Display name */
  name: string;
  /** Raw SVG logo (no sizing classes — sized by parent) */
  logo: React.ReactNode;
}

// --- Raw SVG Logos (no size classes, inherit fill from parent) ---

const MetaLogo = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 .706.07 1.369.21 1.973a6.624 6.624 0 0 0 .265.86 5.297 5.297 0 0 0 .371.761c.696 1.159 1.818 1.927 3.593 1.927 1.497 0 2.633-.671 3.965-2.444.76-1.012 1.144-1.626 2.663-4.32l.756-1.339.186-.325c.061.1.121.196.183.3l2.152 3.595c.724 1.21 1.665 2.556 2.47 3.314 1.046.987 1.992 1.22 3.06 1.22 1.075 0 1.876-.355 2.455-.843a3.743 3.743 0 0 0 .81-.973c.542-.939.861-2.127.861-3.745 0-2.72-.681-5.357-2.084-7.45-1.282-1.912-2.957-2.93-4.716-2.93-1.047 0-2.088.467-3.053 1.308-.652.57-1.257 1.29-1.82 2.05-.69-.875-1.335-1.547-1.958-2.056-1.182-.966-2.315-1.303-3.454-1.303zm10.16 2.053c1.147 0 2.188.758 2.992 1.999 1.132 1.748 1.647 4.195 1.647 6.4 0 1.548-.368 2.9-1.839 2.9-.58 0-1.027-.23-1.664-1.004-.496-.601-1.343-1.878-2.832-4.358l-.617-1.028a44.908 44.908 0 0 0-1.255-1.98c.07-.109.141-.224.211-.327 1.12-1.667 2.118-2.602 3.358-2.602zm-10.201.553c1.265 0 2.058.791 2.675 1.446.307.327.737.871 1.234 1.579l-1.02 1.566c-.757 1.163-1.882 3.017-2.837 4.338-1.191 1.649-1.81 1.817-2.486 1.817-.524 0-1.038-.237-1.383-.794-.263-.426-.464-1.13-.464-2.046 0-2.221.63-4.535 1.66-6.088.454-.687.964-1.226 1.533-1.533a2.264 2.264 0 0 1 1.088-.285z"/>
  </svg>
);

const GoogleLogo = () => (
  <svg viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const TikTokLogo = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.44 6.28 6.28 0 001.86-4.48V8.73a8.32 8.32 0 004.87 1.56v-3.5a4.85 4.85 0 01-1.15-.1z"/>
  </svg>
);

const PinterestLogo = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0a12 12 0 00-4.37 23.17c-.1-.94-.2-2.4.04-3.44l1.4-5.96s-.36-.72-.36-1.78c0-1.66.96-2.9 2.17-2.9 1.02 0 1.52.77 1.52 1.69 0 1.03-.66 2.57-.99 3.99-.28 1.19.6 2.16 1.77 2.16 2.12 0 3.76-2.24 3.76-5.47 0-2.86-2.06-4.86-5-4.86-3.4 0-5.4 2.55-5.4 5.2 0 1.02.4 2.13.89 2.73a.36.36 0 01.08.34l-.33 1.36c-.05.22-.18.27-.4.16-1.5-.7-2.43-2.89-2.43-4.65 0-3.78 2.75-7.26 7.93-7.26 4.17 0 7.4 2.97 7.4 6.93 0 4.14-2.6 7.46-6.23 7.46-1.22 0-2.36-.63-2.75-1.37l-.75 2.85c-.27 1.04-1 2.35-1.49 3.15A12 12 0 1012 0z"/>
  </svg>
);

const TwitterLogo = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// --- Brand Config ---

export const platformBrand: Record<PlatformKey, PlatformBrandConfig> = {
  meta: {
    bg: 'bg-[#0081FB]',
    hover: 'hover:bg-[#006FDB]',
    color: '#0081FB',
    name: 'Meta (Facebook/Instagram)',
    logo: <MetaLogo />,
  },
  google_ads: {
    bg: 'bg-card',
    hover: 'hover:bg-ground',
    color: '#4285F4',
    name: 'Google Ads',
    logo: <GoogleLogo />,
  },
  tiktok: {
    bg: 'bg-black',
    hover: 'hover:bg-navy-2',
    color: '#000000',
    name: 'TikTok',
    logo: <TikTokLogo />,
  },
  pinterest: {
    bg: 'bg-[#E60023]',
    hover: 'hover:bg-[#CC001F]',
    color: '#E60023',
    name: 'Pinterest',
    logo: <PinterestLogo />,
  },
  twitter: {
    bg: 'bg-black',
    hover: 'hover:bg-navy-2',
    color: '#000000',
    name: 'Twitter / X',
    logo: <TwitterLogo />,
  },
};

// --- Size presets ---

const sizeClasses = {
  '2xs': 'w-4 h-4',
  xs: 'w-6 h-6',
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-12 h-12',
  xl: 'w-16 h-16',
  '2xl': 'w-24 h-24',
};

const logoSizeClasses = {
  '2xs': '[&_svg]:w-2.5 [&_svg]:h-2.5',
  xs: '[&_svg]:w-3.5 [&_svg]:h-3.5',
  sm: '[&_svg]:w-5 [&_svg]:h-5',
  md: '[&_svg]:w-5 [&_svg]:h-5',
  lg: '[&_svg]:w-7 [&_svg]:h-7',
  xl: '[&_svg]:w-9 [&_svg]:h-9',
  '2xl': '[&_svg]:w-12 [&_svg]:h-12',
};

export type PlatformLogoSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

// --- Components ---

interface PlatformLogoProps {
  platform: PlatformKey;
  size?: PlatformLogoSize;
  className?: string;
}

/**
 * Renders a platform logo inside a colored rounded container.
 */
export const PlatformLogo: React.FC<PlatformLogoProps> = ({
  platform,
  size = 'md',
  className = '',
}) => {
  const brand = platformBrand[platform];
  if (!brand) return null;

  const textColor = platform === 'tiktok' || platform === 'twitter'
    ? 'text-white'
    : platform === 'google_ads'
      ? ''
      : 'text-white';

  const roundedClass = size === '2xs' ? 'rounded' : 'rounded-lg';
  return (
    <div className={`${sizeClasses[size]} ${brand.bg} ${roundedClass} flex items-center justify-center ${logoSizeClasses[size]} ${textColor} ${className}`}>
      {brand.logo}
    </div>
  );
};

/**
 * Get the platform display name.
 */
export const getPlatformName = (platform: string): string => {
  return (platformBrand as Record<string, PlatformBrandConfig>)[platform]?.name || platform;
};

export default PlatformLogo;
